import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search, X } from 'lucide-react';
import { useResizeObserver, useOnClickOutside } from 'usehooks-ts';
import { Button, DidYouMean, Divider } from '@/components';
import { useSearchContext } from '@/context';
import { useDeviceType } from '@/hooks';
import { trackGAEvent } from '@/services';

type SuggestionMode = 'idle' | 'typing';

export interface SearchBarProps {
  clickableAreaRefs?: Array<React.RefObject<HTMLElement | null>>;
}

export const SearchBar = ({ clickableAreaRefs }: SearchBarProps) => {
  const { searchQuery, suggestionsQuery, setSuggestionsQuery, setSearchAndSuggestionQuery, facet, suggestionResults } =
    useSearchContext();
  const { isDesktop } = useDeviceType();
  const navigate = useNavigate();
  const [searchStarted, setSearchStarted] = useState(false);
  const [suggestionMode, setSuggestionMode] = useState<SuggestionMode>('idle');

  const searchBarRef = useRef<HTMLFormElement | null>(null);
  const { height: searchBarHeight, width: searchBarWidth } = useResizeObserver<HTMLFormElement>({
    ref: searchBarRef as React.RefObject<HTMLFormElement>,
    box: 'border-box',
  });

  const insideRefs = [...(clickableAreaRefs ?? []), searchBarRef];

  useOnClickOutside<HTMLElement>(insideRefs as Array<React.RefObject<HTMLElement>>, () => setSuggestionMode('idle'));

  const hasSuggestions = (suggestionResults?.results?.length ?? 0) > 0;

  const showSuggestions = suggestionMode === 'typing' && (hasSuggestions || suggestionResults?.didYouMean);

  const navigateAndSearch = (query: string) => {
    navigate({
      to: facet === 'all' ? '/results' : '/results/$facet',
      params: facet === 'all' ? {} : { facet },
      search: { q: query },
    });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const input = suggestionsQuery.trim();

    (e.target as HTMLElement).blur();

    setSuggestionMode('idle');
    navigateAndSearch(input);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!searchStarted) {
      trackGAEvent({ event: 'search_session_start' });
      setSearchStarted(true);
    }
    setSuggestionsQuery(e.target.value);
    setSuggestionMode('typing');
  };

  const handleClear = () => {
    setSearchAndSuggestionQuery('');
    setSuggestionMode('idle');
  };

  const onSuggestionSelected = (suggestion: string) => {
    setSuggestionsQuery(suggestion);
    setSuggestionMode('idle');
    navigateAndSearch(suggestion);
  };

  const onSuggestionEnter = (event: React.KeyboardEvent<HTMLElement>, suggestion: string) => {
    if (event.code === 'Enter') {
      onSuggestionSelected(suggestion);
    }
  };

  useEffect(() => {
    if (suggestionsQuery && !searchQuery) {
      setSuggestionMode('typing');
    }
  }, [facet]);

  return (
    <form
      ref={searchBarRef}
      onSubmit={handleSubmit}
      className="focus-within: relative flex h-14 w-full flex-row justify-between gap-0 space-x-0 rounded-lg border-2 border-black
        bg-white ring-0 ring-deep-blue-100 transition-all duration-300 ease-in-out focus-within:border-deep-blue
        focus-within:ring-2 hover:border-deep-blue"
    >
      {isDesktop && <Search className="mx-5 my-3" />}
      <input
        placeholder={facet === 'all' ? 'Find courses, people, events, news ...' : `Find ${facet.toLowerCase()}`}
        autoComplete="off"
        onChange={handleInputChange}
        type="search"
        id="search"
        name="q"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSubmit(e);
          }
        }}
        value={suggestionsQuery}
        className="line-clamp-1 h-full w-full text-ellipsis rounded-lg bg-white p-5 pr-14 text-base text-secondary-grey-01 outline-none
          lg:pl-0 lg:text-lg lg:text-black [&::-webkit-search-cancel-button]:hidden"
        maxLength={200}
      />
      {suggestionsQuery && (
        <Button
          onClick={handleClear}
          className="after:contents-[''] absolute right-[70px] top-3.5 my-auto size-6 border-0 border-none after:absolute after:-inset-1
            after:h-[calc(100%_+_(4px_*_2))] after:w-[calc(100%_+_(4px_*_2))] after:border-2 after:border-transparent
            hover:bg-primary hover:text-white focus-visible:bg-primary focus-visible:text-white focus-visible:ring-0
            focus-visible:after:border-magenta md:right-[105px] lg:right-36"
          size="icon"
          variant="muted"
        >
          <X className="size-4" />
        </Button>
      )}
      {showSuggestions && (
        <div
          className="absolute z-[999] rounded-b-lg bg-white shadow-[0_2px_40px_0_rgba(0,0,0,0.20)]"
          style={{ top: searchBarHeight ?? 0, width: searchBarWidth ?? 0 }}
        >
          {suggestionResults?.didYouMean && (
            <>
              <div className="bg-white px-6">
                <DidYouMean
                  className="py-5"
                  onDidYouMeanClick={() => onSuggestionSelected(suggestionResults.didYouMean!)}
                  onDidYouMeanEnter={(e) => onSuggestionEnter(e, suggestionResults.didYouMean!)}
                  didYouMeanText={suggestionResults.didYouMean}
                />
                <Divider />
              </div>
            </>
          )}
          {hasSuggestions && (
            <ul className="flex flex-col items-start rounded-b-lg">
              {suggestionResults?.results.map((suggestion) => {
                return (
                  <li
                    className="flex items-center justify-between self-stretch px-6 py-5 hover:cursor-pointer hover:bg-deep-blue--light
                      hover:text-deep-blue focus:bg-deep-blue--light focus:text-deep-blue"
                    key={suggestion.text}
                    role="option"
                    onClick={() => onSuggestionSelected(suggestion.text)}
                    onKeyUp={(e) => onSuggestionEnter(e, suggestion.text)}
                    tabIndex={0}
                  >
                    <span className="flex items-center gap-[16px]">
                      <Search size={18} className="min-h-[18px] min-w-[18px]" />
                      {suggestion.text}
                    </span>
                    <span>{suggestion.category}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <Button
        type="submit"
        className="rounded-l-none border-y-0 border-l-2 border-r-0 border-deep-blue px-5 text-base hover:bg-deep-blue-50 lg:px-10"
      >
        {isDesktop ? 'Search' : <Search className="size-5" />}
      </Button>
    </form>
  );
};
